/**
 * ⓘ Details — everything the header and the deleted footer used to print that
 * is reference material rather than a reading (spec §B2): the run's
 * parameters, the offset convention, the source attribution, the method and
 * scope sentences, and any KPI the header had to hide for width.
 *
 * A definition list whose values WRAP. The header chips truncated a standard's
 * citation to "Dimensions per Transport Canada E-…", which is the one string
 * that has to be quotable in full; here it has a popover's width and as many
 * lines as it needs.
 *
 * Hidden KPIs: pass the SAME `DockKpiItem[]` the header's `DockKpiStrip` gets.
 * Rendered inside the header (in `DockPanelButtons`' `details` slot), this
 * reads the header's measured width and lists exactly the items the strip's
 * container queries hid — `isKpiHidden` is the strip's own threshold table.
 */
import type { ReactNode } from 'react';

import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { type DockKpiItem, isKpiHidden, useDockHeaderWidth } from './DockHeader';
import { DOCK_CONTROL, DOCK_TEXT } from './dockStyles';

export interface DockDetailsRow {
  key: string;
  /** The term. Omit for a full-width sentence (the method, the scope). */
  label?: string;
  value: ReactNode;
  testId?: string;
  /** Render the value in the station mono face. */
  mono?: boolean;
}

export interface DockDetailsSection {
  key: string;
  /** "Run", "Parameters", "Source". */
  title: string;
  rows: DockDetailsRow[];
  testId?: string;
}

export interface DockDetailsPopoverProps {
  /** The ⓘ trigger's `data-testid` — `openParams()` in the dock tests clicks it. */
  triggerTestId?: string;
  /** The content's `data-testid`. */
  testId?: string;
  /** The popover's heading — the dock's title or the run's name. */
  title?: string;
  sections: DockDetailsSection[];
  /** The header's KPI items; the ones hidden at the current width are listed. */
  kpis?: DockKpiItem[];
  /** Trigger name; defaults to the shared `dock.details` copy. */
  label?: string;
  /** Controlled open state (tests, or a dock that opens it from a link). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const DockDetailsPopover = ({
  triggerTestId,
  testId,
  title,
  sections,
  kpis,
  label,
  open,
  onOpenChange,
}: DockDetailsPopoverProps) => {
  const { t } = useTranslation('project');
  const headerWidth = useDockHeaderWidth();
  const hiddenKpis = (kpis ?? []).filter((k) => isKpiHidden(k.priority, headerWidth));
  const name = label ?? t('dock.details');
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type='button'
          data-testid={triggerTestId}
          aria-label={name}
          title={t('dock.detailsHelp')}
          className={DOCK_CONTROL.iconButton}
        >
          <Info className={DOCK_CONTROL.icon} aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align='end'
        collisionPadding={8}
        data-testid={testId}
        aria-label={title ?? name}
        className='sv-palette flex max-h-[min(70vh,36rem)] w-[22rem] flex-col gap-3 overflow-y-auto border-slate-200 bg-white p-3 text-xs text-slate-700 shadow-lg dark:border-stone-700 dark:bg-stone-900 dark:text-stone-200'
      >
        {title && <p className='text-[13px] leading-5 font-semibold text-slate-900 dark:text-white'>{title}</p>}
        {hiddenKpis.length > 0 && (
          <section data-testid={testId ? `${testId}-kpis` : undefined} className='flex flex-col gap-1.5'>
            <h3 className={cn(DOCK_TEXT.metaMuted, 'font-medium')}>{t('dock.moreFigures')}</h3>
            <div className='flex flex-wrap gap-x-5 gap-y-2'>
              {hiddenKpis.map((k) => (
                <div key={k.key}>{k.node}</div>
              ))}
            </div>
          </section>
        )}
        {sections.map((s) => (
          <section key={s.key} data-testid={s.testId} className='flex flex-col gap-1.5'>
            <h3 className={cn(DOCK_TEXT.metaMuted, 'font-medium')}>{s.title}</h3>
            <dl className='grid grid-cols-[minmax(6rem,auto)_1fr] gap-x-3 gap-y-1'>
              {s.rows.map((r) =>
                r.label ? (
                  <div key={r.key} className='contents'>
                    <dt className={DOCK_TEXT.metaMuted}>{r.label}</dt>
                    <dd data-testid={r.testId} className={cn('min-w-0 break-words', r.mono && 'sv-sta')}>
                      {r.value}
                    </dd>
                  </div>
                ) : (
                  // A sentence, not a term: it spans both columns. Still a
                  // `dd` (a `dl` may hold only dt/dd groups) with no term.
                  <dd
                    key={r.key}
                    data-testid={r.testId}
                    className={cn('col-span-2 min-w-0 break-words text-slate-600 dark:text-stone-300', r.mono && 'sv-sta')}
                  >
                    {r.value}
                  </dd>
                ),
              )}
            </dl>
          </section>
        ))}
      </PopoverContent>
    </Popover>
  );
};
